'use client'

import React from 'react'
import { PatternDrawing } from '../types/drawing'

interface PatternStatusProps {
  pattern: PatternDrawing
  isValidating?: boolean
  validationResult?: {
    isValid: boolean
    confidence: number
    errors: string[]
  }
  onClose?: () => void
}

const PatternStatus: React.FC<PatternStatusProps> = ({
  pattern,
  isValidating = false,
  validationResult,
  onClose
}) => {
  const getStatusColor = () => {
    if (isValidating) return '#fbbf24' // yellow
    if (!validationResult) return '#6b7280' // gray
    if (validationResult.isValid) return '#10b981' // green
    return '#ef4444' // red
  }

  const getStatusIcon = () => {
    if (isValidating) return '⏳'
    if (!validationResult) return '❓'
    if (validationResult.isValid) return '✅'
    return '❌'
  }

  const getStatusText = () => {
    if (isValidating) return 'Validating pattern...'
    if (!validationResult) return 'Pattern pending validation'
    if (validationResult.isValid) {
      const confidencePercent = Math.round(validationResult.confidence * 100)
      return `Valid pattern (${confidencePercent}% confidence)`
    }
    return `Invalid pattern: ${validationResult.errors.join(', ')}`
  }

  const getQualityBadge = () => {
    if (!validationResult?.isValid) return null
    
    const confidence = validationResult.confidence
    let label = 'Poor'
    let color = '#ef4444'
    
    if (confidence >= 0.9) {
      label = 'Excellent'
      color = '#10b981'
    } else if (confidence >= 0.8) {
      label = 'Good'
      color = '#059669'
    } else if (confidence >= 0.7) {
      label = 'Fair'
      color = '#fbbf24'
    }
    
    return (
      <span 
        className="pattern-quality-badge"
        style={{ 
          background: color, 
          color: 'white', 
          padding: '2px 6px', 
          borderRadius: '4px', 
          fontSize: '10px',
          fontWeight: 'bold',
          marginLeft: '8px'
        }}
      >
        {label}
      </span>
    )
  }

  return (
    <div 
      className="pattern-status"
      style={{
        position: 'absolute',
        top: '10px',
        right: '10px',
        background: 'rgba(0, 0, 0, 0.8)',
        backdropFilter: 'blur(10px)',
        color: 'white',
        padding: '8px 12px',
        borderRadius: '8px',
        fontSize: '12px',
        fontWeight: '500',
        border: `2px solid ${getStatusColor()}`,
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
        zIndex: 1000,
        maxWidth: '300px',
        wordBreak: 'break-word'
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <span style={{ marginRight: '6px', fontSize: '14px' }}>{getStatusIcon()}</span>
          <span style={{ 
            color: getStatusColor(), 
            fontWeight: 'bold', 
            textTransform: 'uppercase',
            fontSize: '10px',
            letterSpacing: '0.5px'
          }}>
            {pattern.type.replace(/-/g, ' ')}
          </span>
          {getQualityBadge()}
        </div>
        {onClose && (
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              border: 'none',
              color: 'white',
              borderRadius: '4px',
              padding: '2px 6px',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: 'bold',
              lineHeight: 1
            }}
          >
            ×
          </button>
        )}
      </div>
      
      <div style={{ 
        color: getStatusColor(),
        lineHeight: '1.3'
      }}>
        {getStatusText()}
      </div>
      
      {validationResult?.isValid && (
        <div style={{ 
          marginTop: '6px', 
          fontSize: '10px', 
          color: '#94a3b8',
          fontStyle: 'italic'
        }}>
          Pattern confirmed with {pattern.points?.length || 0} points
        </div>
      )}
    </div>
  )
}

export default React.memo(PatternStatus) 